// Change MPIN (§13.7, §17.10): current MPIN → new MPIN → confirm → OTP →
// done, with the reference shown at the end.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import {
  requestProfileOtp,
  resendProfileOtp,
  validateChangeMpin,
  verifyAndChangeMpin,
} from '@/api/profile';
import { ApiError } from '@/api/client';
import { isEasyPin } from '@/utils/phase2Validation';
import Button from '@/components/ui/Button/Button';
import PinPad from '@/components/common/auth/PinPad';
import OtpSheet from '@/components/common/auth/OtpSheet';
import './profile.css';

type Step = 'CURRENT' | 'NEW' | 'CONFIRM' | 'OTP' | 'DONE';

const ChangeMpin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('CURRENT');
  const [currentMpin, setCurrentMpin] = useState('');
  const [newMpin, setNewMpin] = useState('');
  const [authId, setAuthId] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(180);
  const [otpSessionKey, setOtpSessionKey] = useState(0);
  const [pinError, setPinError] = useState('');
  const [pinResetKey, setPinResetKey] = useState(0);
  const [reference, setReference] = useState('');

  const failPad = (message: string) => {
    setPinError(message);
    setPinResetKey((k) => k + 1);
  };

  const handlePin = async (pin: string) => {
    setPinError('');
    if (step === 'CURRENT') {
      setCurrentMpin(pin);
      setStep('NEW');
    } else if (step === 'NEW') {
      if (isEasyPin(pin)) {
        failPad('That MPIN is too easy to guess.');
        return;
      }
      try {
        await validateChangeMpin(currentMpin, pin);
      } catch (err) {
        failPad(err instanceof ApiError ? err.message : 'Could not validate the MPIN.');
        if (err instanceof ApiError && err.code === 'MPIN_INCORRECT') setStep('CURRENT');
        return;
      }
      setNewMpin(pin);
      setStep('CONFIRM');
    } else if (step === 'CONFIRM') {
      if (pin !== newMpin) {
        failPad('MPINs do not match.');
        return;
      }
      try {
        const session = await requestProfileOtp();
        setAuthId(session.authId);
        setOtpExpiry(session.expiresInSeconds);
        setOtpSessionKey(Date.now());
        setStep('OTP');
      } catch (err) {
        failPad(err instanceof ApiError ? err.message : 'Could not send the OTP.');
      }
    }
  };

  const handleOtpVerify = async (code: string) => {
    const { reference: ref } = await verifyAndChangeMpin(authId, newMpin, code);
    setReference(ref);
    setStep('DONE');
  };

  const handleOtpResend = async () => {
    const { expiresInSeconds } = await resendProfileOtp(authId);
    return expiresInSeconds;
  };

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => navigate('/profile/settings')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Change MPIN</h2>
      </div>

      {step !== 'DONE' && (
        <p className="nova-review-note">
          Enter your current MPIN, pick a new six-digit MPIN, then confirm the change with an OTP sent to your
          registered mobile.
        </p>
      )}

      {step === 'DONE' && (
        <div className="nova-profile-success">
          <FontAwesomeIcon icon={faCircleCheck} size="3x" className="nova-outcome-ok" />
          <h3>MPIN changed</h3>
          <p>
            Your MPIN has been changed. Reference {reference}.
          </p>
          <Button onClick={() => navigate('/profile/settings')}>Done</Button>
        </div>
      )}

      <PinPad
        show={step === 'CURRENT'}
        variant="MPIN"
        title="Enter your current MPIN"
        error={pinError}
        resetKey={pinResetKey}
        onComplete={(pin) => void handlePin(pin)}
        onClose={() => navigate('/profile/settings')}
      />
      <PinPad
        show={step === 'NEW'}
        variant="MPIN"
        title="Enter a new MPIN"
        subtitle="Six digits. Not a sequence, a repeat or your date of birth."
        error={pinError}
        resetKey={pinResetKey}
        onComplete={(pin) => void handlePin(pin)}
        onClose={() => navigate('/profile/settings')}
      />
      <PinPad
        show={step === 'CONFIRM'}
        variant="MPIN"
        title="Re-enter the new MPIN"
        error={pinError}
        resetKey={pinResetKey}
        onComplete={(pin) => void handlePin(pin)}
        onClose={() => navigate('/profile/settings')}
      />
      <OtpSheet
        show={step === 'OTP'}
        approvalLine="Approve MPIN change"
        sessionKey={otpSessionKey}
        expiresInSeconds={otpExpiry}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onClose={() => navigate('/profile/settings')}
      />
    </div>
  );
};

export default ChangeMpin;
